import React, { useState, useEffect, useRef } from 'react';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useAuth } from '../../../../contexts/AuthContext';
import apiService from '../../../../services/api';
import { ApiResponse } from '../../../../types';
import SidePanel from '../../../UI/SidePanel';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import customDropdownStyles from '../../../../styles/CustomDropdown.module.css';

interface BonusPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const BonusPointsModal: React.FC<BonusPointsModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const notificationService = useNotification();
  const { user } = useAuth();

  // User search state (similar to approver search in TaskForm)
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const userSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [points, setPoints] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Load users when dropdown opens
  useEffect(() => {
    if (userDropdownOpen && allUsers.length === 0) {
      loadUsers('a'); // Load users starting with 'a' as initial set
    }
  }, [userDropdownOpen]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!userDropdownOpen) {
      setUserSearchQuery('');
    }
  }, [userDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userDropdownOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUser(null);
      setPoints('');
      setReason('');
      setUserSearchQuery('');
      setAllUsers([]);
      setUserDropdownOpen(false);
    }
  }, [isOpen]);

  const loadUsers = async (query: string) => {
    setUserSearchLoading(true);
    try {
      const searchQuery = query.trim();
      let response;

      if (searchQuery.length >= 2) {
        response = await apiService.searchUsers(searchQuery);
      } else if (searchQuery.length === 1) {
        response = await apiService.searchUsers(searchQuery);
      } else {
        response = await apiService.searchUsers('a');
      }

      if (response.status === 'success') {
        const users = response.data || [];
        setAllUsers(users);
      } else {
        setAllUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setAllUsers([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const formatDesignation = (designation: string | null | undefined): string => {
    if (!designation) return '';
    return designation.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const getUserDisplayName = (user: any): string => {
    const name = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const designation = formatDesignation(user.designation);
    return designation ? `${name} (${designation})` : name;
  };

  const getFilteredUsers = () => {
    const query = userSearchQuery.toLowerCase().trim();

    return allUsers.filter((userItem: any) => {
      // Exclude already selected user
      if (selectedUser && String(userItem.id) === String(selectedUser.id)) {
        return false;
      }

      // Filter by search query - if no query, show all
      if (!query) return true;

      const fullName = `${userItem.first_name || ''} ${userItem.last_name || ''}`.toLowerCase();
      const email = (userItem.email || '').toLowerCase();
      const designation = formatDesignation(userItem.designation).toLowerCase();
      const displayName = getUserDisplayName(userItem).toLowerCase();
      const userId = (userItem.id || '').toLowerCase();

      return fullName.includes(query) ||
        email.includes(query) ||
        designation.includes(query) ||
        displayName.includes(query) ||
        userId.includes(query);
    });
  };

  const selectUser = (userItem: any) => {
    setSelectedUser(userItem);
    setUserDropdownOpen(false);
    setUserSearchQuery('');
  };

  const removeSelectedUser = () => {
    setSelectedUser(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser || !points || !reason.trim()) {
      notificationService.error('Please fill in all fields');
      return;
    }

    const pointsValue = parseInt(points);
    if (isNaN(pointsValue) || pointsValue <= 0) {
      notificationService.error('Please enter a valid positive number of points');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.addBonusPointsToUser({
        user_id: selectedUser.id,
        points: pointsValue,
        reason: reason.trim()
      }) as ApiResponse;

      if (response.status === 'success') {
        notificationService.success(response.message || 'Bonus points added successfully');
        // Reset form
        setSelectedUser(null);
        setPoints('');
        setReason('');
        onSuccess?.();
        onClose();
      } else {
        notificationService.error(response.message || 'Failed to add bonus points');
      }
    } catch (error: any) {
      console.error('Error adding bonus points:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to add bonus points. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title="Add Bonus Points">
      <form onSubmit={handleSubmit} className={sidePanelStyles.form}>
        {/* User Selection */}
        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>
            Select User <span className="required">*</span>
          </label>

          {/* User Dropdown with Search */}
          <div ref={userDropdownRef} style={{ position: 'relative' }}>
            {selectedUser ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                backgroundColor: 'var(--gray-lighter)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  {`${selectedUser.first_name?.[0] || ''}${selectedUser.last_name?.[0] || ''}`.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', color: 'var(--dark)', marginBottom: '0.25rem' }}>
                    {getUserDisplayName(selectedUser)}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                    {selectedUser.email}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeSelectedUser}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--danger)'
                  }}
                  title="Remove user"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <div
                  onClick={() => {
                    setUserDropdownOpen(!userDropdownOpen);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: '44px'
                  }}
                >
                  <span style={{
                    color: userSearchQuery ? 'var(--dark)' : 'var(--gray)',
                    fontSize: '0.875rem'
                  }}>
                    {userSearchQuery
                      ? userSearchQuery
                      : 'Click to search and select a user...'}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      transform: userDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>

                {/* Dropdown Menu */}
                {userDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    marginTop: '4px',
                    maxHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Search Input */}
                    <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-light)' }}>
                      <input
                        type="text"
                        placeholder="Search by name, email, designation, or ID..."
                        value={userSearchQuery}
                        onChange={(e) => {
                          const query = e.target.value;
                          setUserSearchQuery(query);

                          // Clear previous timeout
                          if (userSearchTimeoutRef.current) {
                            clearTimeout(userSearchTimeoutRef.current);
                          }

                          // Debounce search
                          userSearchTimeoutRef.current = setTimeout(() => {
                            const trimmedQuery = query.trim();
                            if (trimmedQuery.length >= 1) {
                              loadUsers(trimmedQuery);
                            } else if (trimmedQuery.length === 0 && allUsers.length > 0) {
                              loadUsers('a');
                            }
                          }, 300);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          fontSize: '0.875rem'
                        }}
                        autoFocus
                      />
                    </div>

                    {/* Dropdown Options */}
                    <div style={{
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}>
                      {userSearchLoading ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--gray)' }}>
                          <span>Loading...</span>
                        </div>
                      ) : getFilteredUsers().length > 0 ? (
                        getFilteredUsers().map((userItem: any) => {
                          const initials = `${userItem.first_name?.[0] || ''}${userItem.last_name?.[0] || ''}`.toUpperCase();
                          const displayName = getUserDisplayName(userItem);
                          return (
                            <div
                              key={userItem.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                selectUser(userItem);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                borderBottom: '1px solid var(--border-light)',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--gray-lighter)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--primary)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                flexShrink: 0
                              }}>
                                {initials}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: '600', color: 'var(--dark)', marginBottom: '0.25rem' }}>
                                  {displayName}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--gray)' }}>
                                  {userItem.email}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--gray)' }}>
                          {userSearchLoading
                            ? 'Searching...'
                            : userSearchQuery.trim().length >= 2
                              ? 'No users found'
                              : userSearchQuery.trim().length === 1
                                ? 'Type at least 2 characters to search...'
                                : 'Start typing to search for users...'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div className={sidePanelStyles.formHelp} style={{ marginTop: '0.5rem' }}>
            <i className="bi bi-info-circle" style={{ color: 'var(--info)', marginRight: '0.3rem' }}></i>
            Search and select the user you want to award bonus points to.
          </div>
        </div>

        {/* Points Input */}
        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>
            Points <span className="required">*</span>
          </label>
          <input
            type="number"
            className={sidePanelStyles.formControl}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            placeholder="Enter points to award"
            min="1"
            step="1"
            required
          />
          <div className={sidePanelStyles.formHelp}>
            Enter the number of bonus points to award. These points will be added to the user's total.
          </div>
        </div>

        {/* Reason Input */}
        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>
            Reason <span className="required">*</span>
          </label>
          <textarea
            className={sidePanelStyles.formControl}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for awarding bonus points (e.g., 'Outstanding performance in Q4', 'Extra effort on project X')"
            rows={4}
            required
          />
          <div className={sidePanelStyles.formHelp}>
            Provide a clear reason for awarding these bonus points. This will be recorded in the point history.
          </div>
        </div>

        {/* Form Actions */}
        <div className={sidePanelStyles.formActions}>
          <button
            type="button"
            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
            disabled={loading || !selectedUser || !points || !reason.trim()}
          >
            {loading ? (
              <>
                <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem', animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Adding...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.5rem' }}>
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Bonus Points
              </>
            )}
          </button>
        </div>
      </form>
    </SidePanel>
  );
};

export default BonusPointsModal;
